import { offlineSyncService } from "../services/offlineSync.service";
import {
  countVehicles,
  countVehiclesBySource,
  getMeta,
  isQuotaError,
  openOfflineDb,
  pruneVehiclesNotInKeepSets,
  pruneVehiclesNotInSet,
  putVehicleBatchAndMeta,
  setMeta,
  deleteOfflineDatabase,
} from "./offlineDb";
import {
  offlineDbName,
  readRememberedOfflineDbName,
  rememberOfflineDbName,
  resolveRepoIdentity,
} from "./offlineIdentity";
import { toStoredVehicle } from "./recordKeys";
import { clearWhatsAppRecipients, saveWhatsAppRecipients } from "./whatsappRecipientsCache";
import confirmationService from "../services/confirmation.service";
import {
  SYNC_BATCH_RETRIES,
  SYNC_BATCH_SIZE,
  SYNC_ID_BATCH_SIZE,
  SYNC_MAX_CONCURRENT,
  SYNC_RETRY_BASE_MS,
} from "./offlineSyncConfig";

export const SYNC_STATUS = {
  IDLE: "idle",
  SYNCING: "syncing",
  COMPLETED: "completed",
  FAILED: "failed",
  OFFLINE_READY: "offline_ready",
};

const META_KEY = "state";
const OVERLAP_MS = 120000;

const listeners = new Set();
let runId = 0;
let abortController = null;
let snapshot = emptySnapshot();

function emptySnapshot() {
  return {
    status: SYNC_STATUS.IDLE,
    statusLabel: "Online",
    total: 0,
    synced: 0,
    remaining: 0,
    percent: 0,
    agencyTotal: 0,
    bankTotal: 0,
    agencySynced: 0,
    bankSynced: 0,
    lastSuccessAt: null,
    error: "",
    warning: "",
    initialComplete: false,
    ready: false,
    dbName: "",
  };
}

function emit() {
  listeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch {
      /* ignore */
    }
  });
}

function setSnapshot(partial) {
  snapshot = { ...snapshot, ...partial };
  emit();
}

export function getOfflineSyncSnapshot() {
  return snapshot;
}

export function subscribeOfflineSync(fn) {
  listeners.add(fn);
  fn(snapshot);
  return () => listeners.delete(fn);
}

export function stopOfflineSync() {
  runId += 1;
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
}

function statusLabel(state) {
  if (state.status === SYNC_STATUS.SYNCING) return "Syncing";
  if (state.status === SYNC_STATUS.OFFLINE_READY) return "Offline Ready";
  if (state.status === SYNC_STATUS.COMPLETED) return "Sync Completed";
  if (state.status === SYNC_STATUS.FAILED) return "Sync Failed";
  return "Offline";
}

function computeProgress(state) {
  const total = Number(state.agencyTotal || 0) + Number(state.bankTotal || 0);
  const synced = Number(state.agencySynced || 0) + Number(state.bankSynced || 0);
  const remaining = Math.max(0, total - synced);
  const percent =
    total > 0 ? Math.min(99, Math.floor((synced / total) * 100)) : 0;
  return { total, synced, remaining, percent };
}

function toUiState(meta, extra = {}) {
  const progress = computeProgress(meta);
  const ready =
    meta.status === SYNC_STATUS.OFFLINE_READY && Boolean(meta.initialComplete);
  const percent = ready ? 100 : progress.percent;
  return {
    ...progress,
    percent,
    status: meta.status,
    statusLabel: statusLabel(meta),
    agencyTotal: meta.agencyTotal || 0,
    bankTotal: meta.bankTotal || 0,
    agencySynced: meta.agencySynced || 0,
    bankSynced: meta.bankSynced || 0,
    lastSuccessAt: meta.lastSuccessAt || null,
    error: meta.error || "",
    warning: meta.warning || "",
    initialComplete: Boolean(meta.initialComplete),
    ready,
    dbName: extra.dbName || snapshot.dbName,
  };
}

function defaultMeta(userId, companyId) {
  return {
    key: META_KEY,
    userId,
    companyId,
    status: SYNC_STATUS.IDLE,
    phase: "records-agency",
    agencyCursor: "",
    bankCursor: "",
    agencyIdCursor: "",
    bankIdCursor: "",
    agencySynced: 0,
    bankSynced: 0,
    agencyTotal: 0,
    bankTotal: 0,
    since: "",
    lastSuccessAt: null,
    lastStartedAt: null,
    initialComplete: false,
    error: "",
    warning: "",
    generation: 0,
    forceFullResync: false,
    agencyRecordsDone: false,
    bankRecordsDone: false,
  };
}

function createLimiter(max) {
  let active = 0;
  const queue = [];
  const pump = () => {
    if (active >= max || !queue.length) return;
    active += 1;
    const { fn, resolve, reject } = queue.shift();
    Promise.resolve()
      .then(fn)
      .then(resolve, reject)
      .finally(() => {
        active -= 1;
        pump();
      });
  };
  return (fn) =>
    new Promise((resolve, reject) => {
      queue.push({ fn, resolve, reject });
      pump();
    });
}

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function isRetryableSyncError(error) {
  if (error?.code === "STALE_SYNC" || error?.code === "ABORTED") return false;
  if (isQuotaError(error)) return false;
  const status = error?.response?.status;
  if (status === 401 || status === 403) return false;
  return true;
}

function isCancelledSyncError(error) {
  return error?.code === "STALE_SYNC" || error?.code === "ABORTED";
}

/** Settle every sibling so a cancelled run cannot surface as an uncaught rejection. */
async function awaitAllSync(promises) {
  const results = await Promise.allSettled(promises);
  let cancelled = null;
  let hard = null;
  const values = [];
  for (let i = 0; i < results.length; i += 1) {
    if (results[i].status === "fulfilled") {
      values.push(results[i].value);
      continue;
    }
    const reason = results[i].reason;
    if (isCancelledSyncError(reason)) cancelled = reason;
    else if (!hard) hard = reason;
    values.push(undefined);
  }
  if (hard) throw hard;
  if (cancelled) throw cancelled;
  return values;
}

function migrateResumePhase(meta) {
  const phase = meta.phase || "records";
  if (phase === "records-agency") {
    meta.phase = "records";
  } else if (phase === "records-bank") {
    meta.phase = "records";
    meta.agencyRecordsDone = true;
  } else if (phase === "prune-agency" || phase === "prune-bank" || phase === "verify") {
    meta.agencyRecordsDone = true;
    meta.bankRecordsDone = true;
  }
}

async function saveMeta(db, meta) {
  await setMeta(db, meta);
  setSnapshot(toUiState(meta, { dbName: db.name }));
}

function assertRun(current) {
  if (current !== runId) {
    const err = new Error("stale-sync");
    err.code = "STALE_SYNC";
    throw err;
  }
}

function throwIfAborted(signal) {
  if (signal?.aborted) {
    const err = new Error("aborted");
    err.code = "ABORTED";
    throw err;
  }
}

async function fetchIdsPage({
  token,
  source,
  cursor,
  signal,
  current,
  limitHttp,
}) {
  let lastError = null;
  for (let attempt = 1; attempt <= SYNC_BATCH_RETRIES; attempt += 1) {
    assertRun(current);
    throwIfAborted(signal);
    try {
      return await limitHttp(() =>
        offlineSyncService.getIds(
          token,
          { source, cursor, limit: SYNC_ID_BATCH_SIZE },
          { signal }
        )
      );
    } catch (error) {
      lastError = error;
      if (!isRetryableSyncError(error) || attempt >= SYNC_BATCH_RETRIES) throw error;
      await sleep(SYNC_RETRY_BASE_MS * 2 ** (attempt - 1));
    }
  }
  throw lastError;
}

async function fetchKeepIds(token, source, signal, current, limitHttp) {
  const keep = new Set();
  let inFlight = fetchIdsPage({
    token,
    source,
    cursor: "",
    signal,
    current,
    limitHttp,
  });

  while (true) {
    assertRun(current);
    throwIfAborted(signal);
    const res = await inFlight;
    const ids = res?.ids || [];
    const hasNext = Boolean(res?.hasNext);
    const nextCursor = res?.nextCursor || "";
    let nextFlight = null;
    if (hasNext && nextCursor) {
      nextFlight = fetchIdsPage({
        token,
        source,
        cursor: nextCursor,
        signal,
        current,
        limitHttp,
      });
    }
    for (let i = 0; i < ids.length; i += 1) keep.add(String(ids[i]));
    if (!hasNext || !nextCursor) break;
    inFlight = nextFlight;
  }
  return keep;
}

async function fetchRecordsPage({
  token,
  source,
  cursor,
  since,
  signal,
  current,
  limitHttp,
}) {
  let lastError = null;
  for (let attempt = 1; attempt <= SYNC_BATCH_RETRIES; attempt += 1) {
    assertRun(current);
    throwIfAborted(signal);
    try {
      return await limitHttp(() =>
        offlineSyncService.getRecords(
          token,
          { source, cursor, since, limit: SYNC_BATCH_SIZE },
          { signal }
        )
      );
    } catch (error) {
      lastError = error;
      if (!isRetryableSyncError(error) || attempt >= SYNC_BATCH_RETRIES) throw error;
      await sleep(SYNC_RETRY_BASE_MS * 2 ** (attempt - 1));
    }
  }
  throw lastError;
}

function mapItemsToStored(items) {
  const stored = [];
  const seen = new Set();
  const list = Array.isArray(items) ? items : [];
  for (let i = 0; i < list.length; i += 1) {
    const row = toStoredVehicle(list[i]);
    if (!row.id || seen.has(row.id)) continue;
    seen.add(row.id);
    stored.push(row);
  }
  return stored;
}

function createWriteChain() {
  let tail = Promise.resolve();
  return (fn) => {
    const run = tail.then(fn, fn);
    tail = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  };
}

async function syncSourceRecords({
  db,
  token,
  source,
  meta,
  signal,
  current,
  limitHttp,
  runIdbWrite,
}) {
  const cursorKey = source === "bank" ? "bankCursor" : "agencyCursor";
  const syncedKey = source === "bank" ? "bankSynced" : "agencySynced";
  const doneKey = source === "bank" ? "bankRecordsDone" : "agencyRecordsDone";
  const since = meta.initialComplete ? meta.since || "" : "";
  if (!since && meta[doneKey]) return;

  let cursor = meta[cursorKey] || "";
  let inFlight = fetchRecordsPage({
    token,
    source,
    cursor,
    since,
    signal,
    current,
    limitHttp,
  });

  while (true) {
    const res = await inFlight;
    const hasNext = Boolean(res?.hasNext);
    const nextCursor = res?.nextCursor || "";
    let nextFlight = null;
    if (hasNext && nextCursor) {
      nextFlight = fetchRecordsPage({
        token,
        source,
        cursor: nextCursor,
        since,
        signal,
        current,
        limitHttp,
      });
    }

    const stored = mapItemsToStored(res?.items);
    try {
      await runIdbWrite(async () => {
        cursor = nextCursor;
        meta[cursorKey] = hasNext ? cursor : "";
        if (!since) {
          meta[syncedKey] = (Number(meta[syncedKey]) || 0) + stored.length;
        }
        meta.error = "";
        await putVehicleBatchAndMeta(db, stored, meta);
        setSnapshot(toUiState(meta, { dbName: db.name }));
      });
    } catch (error) {
      if (nextFlight) nextFlight.catch(() => {});
      throw error;
    }

    if (!hasNext || !nextCursor) break;
    inFlight = nextFlight;
  }

  await runIdbWrite(async () => {
    meta[doneKey] = true;
    meta[cursorKey] = "";
    meta[syncedKey] = await countVehiclesBySource(db, source);
    await saveMeta(db, meta);
  });
}

export async function hydrateOfflineSync(user) {
  const { userId, companyId } = resolveRepoIdentity(user);
  if (!userId || !companyId) {
    setSnapshot(emptySnapshot());
    return;
  }
  const dbName = offlineDbName(userId, companyId);
  try {
    const db = await openOfflineDb(dbName);
    const meta = (await getMeta(db, META_KEY)) || defaultMeta(userId, companyId);
    const localCount = await countVehicles(db);
    if (localCount && meta.agencySynced + meta.bankSynced === 0) {
      meta.agencySynced = localCount;
    }
    setSnapshot(toUiState(meta, { dbName }));
  } catch {
    setSnapshot(emptySnapshot());
  }
}

export async function startOfflineSync({ token, user }) {
  const { userId, companyId } = resolveRepoIdentity(user);
  if (!token || !userId || !companyId) {
    setSnapshot({
      ...emptySnapshot(),
      status: SYNC_STATUS.FAILED,
      statusLabel: "Sync Failed",
      error: "Missing user or company for offline sync.",
    });
    return;
  }

  stopOfflineSync();
  const current = runId;
  abortController = new AbortController();
  const { signal } = abortController;
  const dbName = offlineDbName(userId, companyId);

  let db;
  try {
    db = await openOfflineDb(dbName);
  } catch (error) {
    setSnapshot({
      ...emptySnapshot(),
      status: SYNC_STATUS.FAILED,
      statusLabel: "Sync Failed",
      error: isQuotaError(error)
        ? "This browser does not have enough storage for offline vehicle data."
        : "Could not open local vehicle database.",
    });
    return;
  }

  let meta = (await getMeta(db, META_KEY)) || defaultMeta(userId, companyId);
  if (meta.userId !== userId || meta.companyId !== companyId) {
    meta = defaultMeta(userId, companyId);
  }
  meta.generation = current;
  meta.status = SYNC_STATUS.SYNCING;
  meta.error = "";
  meta.warning = meta.initialComplete
    ? ""
    : "Complete dataset is not yet available offline.";
  meta.lastStartedAt = new Date().toISOString();
  migrateResumePhase(meta);
  if (meta.forceFullResync) {
    meta.since = "";
    meta.forceFullResync = false;
    meta.agencyCursor = "";
    meta.bankCursor = "";
    meta.agencyRecordsDone = false;
    meta.bankRecordsDone = false;
    meta.phase = "records";
  } else if (meta.initialComplete && meta.lastSuccessAt) {
    const sinceDate = new Date(meta.lastSuccessAt).getTime() - OVERLAP_MS;
    meta.since = new Date(Math.max(0, sinceDate)).toISOString();
    meta.agencyCursor = "";
    meta.bankCursor = "";
    meta.agencyRecordsDone = false;
    meta.bankRecordsDone = false;
    meta.phase = "records";
  } else {
    meta.since = "";
  }
  await saveMeta(db, meta);

  try {
    try {
      const recipientsRes = await confirmationService.getWhatsAppRecipients(token);
      const recipients = Array.isArray(recipientsRes?.data?.adminRecipients)
        ? recipientsRes.data.adminRecipients
        : Array.isArray(recipientsRes?.adminRecipients)
          ? recipientsRes.adminRecipients
          : [];
      if (recipients.length) {
        saveWhatsAppRecipients({ userId, companyId, recipients });
      }
    } catch {
      /* keep previously cached admin / role numbers */
    }

    const timings = { recordsMs: 0, idsMs: 0, pruneMs: 0, verifyMs: 0 };
    const syncStartedAt = performance.now();
    const summary = await offlineSyncService.getSummary(token, { signal });
    meta.agencyTotal = Number(summary?.agencyTotal) || 0;
    meta.bankTotal = Number(summary?.bankTotal) || 0;
    await saveMeta(db, meta);

    const limitHttp = createLimiter(SYNC_MAX_CONCURRENT);
    const runIdbWrite = createWriteChain();
    const phase = meta.phase || "records";
    const order = ["records", "prune-agency", "prune-bank", "verify"];
    const startAt = order.includes(phase) ? order.indexOf(phase) : 0;
    let pendingBankKeep = null;
    let prunedBothSources = false;

    for (let i = startAt; i < order.length; i += 1) {
      assertRun(current);
      meta.phase = order[i];
      await saveMeta(db, meta);

      if (order[i] === "records") {
        const tRecords = performance.now();
        await awaitAllSync([
          syncSourceRecords({
            db,
            token,
            source: "agency",
            meta,
            signal,
            current,
            limitHttp,
            runIdbWrite,
          }),
          syncSourceRecords({
            db,
            token,
            source: "bank",
            meta,
            signal,
            current,
            limitHttp,
            runIdbWrite,
          }),
        ]);
        timings.recordsMs = Math.round(performance.now() - tRecords);
      } else if (order[i] === "prune-agency") {
        const tIds = performance.now();
        const [agencyKeep, bankKeep] = await awaitAllSync([
          fetchKeepIds(token, "agency", signal, current, limitHttp),
          fetchKeepIds(token, "bank", signal, current, limitHttp),
        ]);
        timings.idsMs = Math.round(performance.now() - tIds);
        pendingBankKeep = bankKeep;
        const tPrune = performance.now();
        await pruneVehiclesNotInKeepSets(db, {
          agency: agencyKeep,
          bank: bankKeep,
        });
        timings.pruneMs = Math.round(performance.now() - tPrune);
        prunedBothSources = true;
        pendingBankKeep = null;
        meta.agencyIdCursor = "";
        meta.bankIdCursor = "";
        meta.agencySynced = await countVehiclesBySource(db, "agency");
        meta.bankSynced = await countVehiclesBySource(db, "bank");
        await saveMeta(db, meta);
      } else if (order[i] === "prune-bank") {
        if (!prunedBothSources) {
          const keep =
            pendingBankKeep ||
            (await fetchKeepIds(token, "bank", signal, current, limitHttp));
          pendingBankKeep = null;
          const tPrune = performance.now();
          await pruneVehiclesNotInSet(db, "bank", keep);
          timings.pruneMs += Math.round(performance.now() - tPrune);
        }
        meta.bankIdCursor = "";
        if (!prunedBothSources) {
          meta.bankSynced = await countVehiclesBySource(db, "bank");
        }
        await saveMeta(db, meta);
      } else if (order[i] === "verify") {
        const tVerify = performance.now();
        meta.agencySynced = await countVehiclesBySource(db, "agency");
        meta.bankSynced = await countVehiclesBySource(db, "bank");
        const localCount = meta.agencySynced + meta.bankSynced;
        const latest = await offlineSyncService.getSummary(token, { signal });
        meta.agencyTotal = Number(latest?.agencyTotal) || 0;
        meta.bankTotal = Number(latest?.bankTotal) || 0;
        const expected = meta.agencyTotal + meta.bankTotal;
        if (localCount !== expected) {
          timings.verifyMs = Math.round(performance.now() - tVerify);
          timings.totalMs = Math.round(performance.now() - syncStartedAt);
          console.info("[offline-sync] phase-ms verify-mismatch", timings);
          meta.status = SYNC_STATUS.FAILED;
          meta.error = `Could not verify complete dataset (local ${localCount.toLocaleString("en-IN")}, server ${expected.toLocaleString("en-IN")}).`;
          meta.warning = "Complete dataset is not yet available offline.";
          meta.phase = "records";
          meta.agencyCursor = "";
          meta.bankCursor = "";
          meta.agencyRecordsDone = false;
          meta.bankRecordsDone = false;
          meta.forceFullResync = true;
          await saveMeta(db, meta);
          return;
        }
        meta.status = SYNC_STATUS.OFFLINE_READY;
        meta.initialComplete = true;
        meta.lastSuccessAt = new Date().toISOString();
        meta.warning = "";
        meta.error = "";
        meta.phase = "records";
        await saveMeta(db, meta);
        timings.verifyMs = Math.round(performance.now() - tVerify);
        timings.totalMs = Math.round(performance.now() - syncStartedAt);
        console.info("[offline-sync] phase-ms", timings);
        setSnapshot({
          ...toUiState(meta, { dbName }),
          percent: 100,
          synced: localCount,
          remaining: 0,
          ready: true,
        });
      }
    }
  } catch (error) {
    if (error?.code === "STALE_SYNC" || error?.code === "ABORTED") return;
    const quota = isQuotaError(error);
    const expired = error?.response?.status === 401;
    meta.status = SYNC_STATUS.FAILED;
    meta.error = quota
      ? "This device does not have enough storage for the complete vehicle dataset."
      : expired
        ? "Session expired. Sign in again to finish offline sync."
        : error?.response?.data?.message ||
          error?.message ||
          "Sync failed. You can retry or resume.";
    meta.warning = meta.initialComplete
      ? ""
      : "Complete dataset is not yet available offline.";
    try {
      await saveMeta(db, meta);
    } catch {
      setSnapshot({
        ...toUiState(meta, { dbName }),
        status: SYNC_STATUS.FAILED,
        statusLabel: "Sync Failed",
        error: meta.error,
      });
    }
  }
}

export async function wipeOfflineCaches() {
  stopOfflineSync();
  clearWhatsAppRecipients();
  const known = readRememberedOfflineDbName();
  rememberOfflineDbName("");
  await deleteOfflineDatabase(known);
  setSnapshot(emptySnapshot());
}
